import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  fetchAvailablePropBots,
  reassignBotAccount,
  disconnectAccountTemporarily,
  reconnectAccount,
  ReassignmentOptions,
  ReassignmentResult
} from '@/services/bot-account-reassignment-service';
import { PropBot } from '@/types/prop-bots';

export interface UseAccountReassignmentProps {
  currentBotId?: string;
}

export const useBotAccountReassignment = ({ currentBotId }: UseAccountReassignmentProps = {}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isReassignDialogOpen, setIsReassignDialogOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  // Check if user is admin
  const isAdmin = user?.user_metadata?.role === 'admin' || user?.app_metadata?.role === 'admin';

  // Fetch available prop bots for reassignment
  const { 
    data: availableBots = [], 
    isLoading: isLoadingBots 
  } = useQuery({
    queryKey: ['availablePropBots', currentBotId],
    queryFn: () => fetchAvailablePropBots(currentBotId),
    enabled: isAdmin && isReassignDialogOpen, // Only load when dialog is open and user is admin
  });

  // Reassignment mutation
  const reassignmentMutation = useMutation({
    mutationFn: async ({ 
      accountId, 
      targetBotId, 
      options 
    }: { 
      accountId: string; 
      targetBotId: string; 
      options: ReassignmentOptions 
    }) => {
      let result: ReassignmentResult;

      try {
        // Optional: Disconnect during change for safety
        if (options.disconnectDuringChange) {
          await disconnectAccountTemporarily(accountId);
        }

        // Perform the reassignment
        result = await reassignBotAccount(accountId, targetBotId, options);

        // Optional: Reconnect after successful reassignment
        if (options.disconnectDuringChange && result.success) {
          await reconnectAccount(accountId);
        }

        return result;
      } catch (error) {
        // If we disconnected and failed, try to reconnect
        if (options.disconnectDuringChange) {
          try {
            await reconnectAccount(accountId);
          } catch (reconnectError) {
            console.error('Failed to reconnect account after failed reassignment:', reconnectError);
          }
        }
        throw error;
      }
    },
    onSuccess: (result: ReassignmentResult) => {
      if (result.success) {
        toast.success('Account reassigned successfully', {
          description: `Trading account moved to new bot configuration`
        });

        // Invalidate relevant queries to refresh the UI
        queryClient.invalidateQueries({ queryKey: ['propBotConnectedAccounts'] });
        queryClient.invalidateQueries({ queryKey: ['propBotAccounts'] });
        queryClient.invalidateQueries({ queryKey: ['detailedBotAccounts'] });
        queryClient.invalidateQueries({ queryKey: ['bot'] });
        
        // Close dialog
        setIsReassignDialogOpen(false);
        setSelectedAccountId(null);
      } else {
        toast.error('Reassignment failed', {
          description: result.error || 'Unknown error occurred'
        });
      }
    },
    onError: (error: any) => {
      console.error('Account reassignment error:', error);
      
      // Handle specific error cases
      if (error.message?.includes('duplicate')) {
        toast.error('Reassignment failed', {
          description: 'API connection already exists for target bot. Please disconnect it first or choose a different account.'
        });
      } else if (error.message?.includes('not found')) {
        toast.error('Reassignment failed', {
          description: 'Account or target bot not found. Please refresh and try again.'
        });
      } else {
        toast.error('Reassignment failed', {
          description: error.message || 'An unexpected error occurred'
        });
      }
    },
  });

  // Helper functions
  const openReassignDialog = (accountId: string) => {
    if (!isAdmin) {
      toast.error('Access denied', {
        description: 'Only administrators can reassign bot accounts'
      });
      return;
    }
    
    setSelectedAccountId(accountId);
    setIsReassignDialogOpen(true);
  };

  const closeReassignDialog = () => {
    setIsReassignDialogOpen(false);
    setSelectedAccountId(null);
  };

  const performReassignment = async (
    targetBotId: string,
    options: ReassignmentOptions
  ): Promise<void> => {
    if (!selectedAccountId) {
      toast.error('No account selected');
      return;
    }

    await reassignmentMutation.mutateAsync({
      accountId: selectedAccountId,
      targetBotId,
      options
    });
  };

  return {
    // State
    isReassignDialogOpen,
    selectedAccountId,
    availableBots,
    isLoadingBots,
    isAdmin,
    
    // Actions
    openReassignDialog,
    closeReassignDialog,
    performReassignment,
    
    // Status
    isReassigning: reassignmentMutation.isPending,
    reassignmentResult: reassignmentMutation.data,
  };
};